import type { ApiErrorData } from '@/api/model';
import { isApiErrorData } from '@/shared/util';

interface PedanticLikeError {
  loc?: unknown;
  msg?: unknown;
}

function isPedanticLikeError(value: unknown): value is PedanticLikeError {
  return typeof value === 'object' && value !== null && 'msg' in value;
}

function formatValidationMessage(msg: unknown): string {
  if (typeof msg === 'string') {
    return msg;
  }

  if (msg === null || msg === undefined) {
    return 'Validation error';
  }

  if (typeof msg === 'number' || typeof msg === 'boolean') {
    return String(msg);
  }

  try {
    return JSON.stringify(msg);
  } catch {
    return 'Validation error';
  }
}

function formatPedanticStyleDetail(err: PedanticLikeError): string {
  const msg = formatValidationMessage(err.msg);
  let loc = err.loc;

  if (Array.isArray(loc) && loc.length > 0 && loc[0] === 'body') {
    loc = loc.slice(1);
  }

  const fieldPath = Array.isArray(loc) && loc.length > 0 ? loc.map(String).join('.') : '';

  return fieldPath ? `${fieldPath}: ${msg}` : msg;
}

function normalizeDetailArray(detail: unknown[], options?: { code?: string }): ApiErrorData {
  const code = options?.code;
  let items: ApiErrorData;

  if (detail.every((item): item is string => typeof item === 'string')) {
    items = detail.map((line) => ({ detail: line }));
  } else if (detail.every((item) => isPedanticLikeError(item) || typeof item === 'string')) {
    items = detail.map((item) =>
      typeof item === 'string' ? { detail: item } : { detail: formatPedanticStyleDetail(item) },
    );
  } else {
    items = detail.map((item) => ({
      detail: typeof item === 'string' ? item : JSON.stringify(item),
    }));
  }

  return code ? items.map((item) => ({ ...item, code })) : items;
}

function normalizeLegacyDetailObject(data: Record<string, unknown>): ApiErrorData | undefined {
  const { detail } = data;
  const code = typeof data.code === 'string' ? data.code : undefined;

  if (typeof detail === 'string') {
    return code ? [{ code, detail }] : [{ detail }];
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return code === undefined
      ? normalizeDetailArray(detail)
      : normalizeDetailArray(detail, { code });
  }

  return undefined;
}

export function normalizeApiErrorResponseBody(data: unknown): ApiErrorData | undefined {
  if (data === null || data === undefined) {
    return undefined;
  }

  if (isApiErrorData(data)) {
    return data;
  }

  if (typeof data === 'object' && data !== null && 'detail' in data) {
    const normalized = normalizeLegacyDetailObject(data as Record<string, unknown>);

    if (normalized) {
      return normalized;
    }
  }

  if (Array.isArray(data) && data.length > 0 && data.every((item) => isPedanticLikeError(item))) {
    return data.map((item) => ({ detail: formatPedanticStyleDetail(item) }));
  }

  return undefined;
}
